import {
  useRef,
  useState,
  useEffect,
  type ChangeEvent,
  type KeyboardEvent,
  type InputHTMLAttributes,
} from 'react';
import styled, { css } from 'styled-components';

import { Text } from '@yoweb/ui/components/typography/Text';
import Spinner from '@yoweb/ui/components/Spinner';
import { Input } from '@yoweb/ui/components/form';
import { useAddressAutocomplete } from '@yoweb/ui/hooks/useAddressAutocomplete';
import {
  getColor,
  getRadii,
  getSpace,
  getZIndex,
  getFontWeight,
} from '@yoweb/ui/styles/utils/theme';
import {
  KEY_ARROW_DOWN,
  KEY_ARROW_UP,
  KEY_ENTER,
  KEY_ESCAPE,
} from '@yoweb/ui/constants/keyboardKey';

type AddressAutocompleteProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'required'> & {
  /* Id value for input, defaults to name */
  id?: string;

  /* Name value for input */
  name: string;

  /* A callback that receives the address that was selected */
  onAddressSelect: (address: Address) => void;

  /* A callback that receives the address changes */
  onAddressInputChanged: (address: string) => void;

  /* Value for the input's label */
  labelText: string;

  /* Initial value of the input */
  initialValue?: string;

  /* Should reset when initialValue changes */
  enableReinitialize?: boolean;

  /* The country to restrict the address picker to */
  countryRestriction?: string;

  /* An optional callback for handling errors */
  onError?: (message: string) => void;

  /* Debounce in ms for the call to the Google Places api */
  debounce?: number;

  /* google places api key */
  placesApiKey: string;

  /**
   * If input is required or not
   */
  isRequired?: boolean;
} & DataTestId;

export type Address = {
  street?: string;
  city?: string;
  state?: string;
  stateIsoCode?: string;
  country?: string;
  countryIsoCode?: string;
  postalCode?: string;
};

/**
 * An address picker for the Google Places Api
 */
const AddressAutocomplete = ({
  countryRestriction,
  debounce = 200,
  enableReinitialize = false,
  id,
  initialValue = '',
  labelText,
  name,
  onAddressInputChanged,
  onAddressSelect,
  onError = () => {},
  onChange = () => {},
  onPaste = () => {},
  inputMode,
  placesApiKey = '',
  isRequired,
  disabled,
  'data-testid': dataTestId,
}: AddressAutocompleteProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [showPlaces, setShowPlaces] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const attributionsElement = useRef<HTMLDivElement>(null);

  const { clearPlaces, getPlaces, isLoading, places, selectPlace, updateValue, value } =
    useAddressAutocomplete({
      attributionsElement,
      countryRestriction,
      debounce,
      enableReinitialize,
      initialValue,
      onAddressInputChanged,
      onAddressSelect,
      onError,
      placesApiKey,
    });

  useEffect(() => {
    setShowPlaces(Boolean(places && places.length > 0));
  }, [places]);

  const handleEnterKey = () => {
    if (activeIndex === null || !places) {
      return;
    }

    const place = places[activeIndex];

    if (place) {
      selectPlace(place.placeId);
    }
  };

  const handleArrowKey = (direction: 'up' | 'down') => {
    if (!places) {
      return;
    }

    if (activeIndex === null) {
      setActiveIndex(0);
      return;
    }

    const index = (direction === 'down' ? activeIndex + 1 : activeIndex - 1) % places.length;
    setActiveIndex(index < 0 ? places.length - 1 : index);
  };

  const onClose = (value?: string) => {
    if (value) {
      setShowPlaces(false);
    } else {
      clearPlaces(value);
    }
    setIsSelecting(false);
    setActiveIndex(null);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case KEY_ENTER:
        event.preventDefault();
        handleEnterKey();
        break;
      case KEY_ARROW_DOWN:
        event.preventDefault();
        handleArrowKey('down');
        break;
      case KEY_ARROW_UP:
        event.preventDefault();
        handleArrowKey('up');
        break;
      case KEY_ESCAPE:
        event.preventDefault();
        onClose();
        break;
    }
  };

  const handleMouseDown = () => setIsSelecting(true);

  return (
    <StyledAutocomplete>
      <Input
        id={id || name}
        value={value}
        autoComplete="no"
        name={name}
        label={labelText}
        icon={isLoading ? <Spinner size="sm" /> : undefined}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          onChange(event);
          updateValue(event.target.value);
        }}
        onPaste={onPaste}
        onBlur={() => {
          if (!isSelecting) {
            onClose(value);
          }
        }}
        onFocus={getPlaces}
        onKeyDown={handleKeyDown}
        data-testid={dataTestId ?? 'address-autocomplete-search'}
        isRequired={isRequired}
        inputMode={inputMode}
        maxLength={255}
        enforceCharacterWidth="half"
        disabled={disabled}
      >
        <PlacesContainer>
          {showPlaces && places && (
            <PlacesDropDown>
              {places.map(({ placeId, highlights }, index) => (
                <DropDownItem
                  id={placeId}
                  key={placeId}
                  role="listbox"
                  tabIndex={index}
                  isActive={index === activeIndex}
                  onClick={() => selectPlace(placeId)}
                  onKeyPress={() => selectPlace(placeId)}
                  onMouseDown={handleMouseDown}
                >
                  {highlights.map(({ shouldHighlight, text }, idx) => (
                    <PlaceText key={idx} shouldHighlight={shouldHighlight}>
                      {text}
                    </PlaceText>
                  ))}
                </DropDownItem>
              ))}
            </PlacesDropDown>
          )}
        </PlacesContainer>
      </Input>

      <Text ref={attributionsElement} variant="muted" size="sm" />
    </StyledAutocomplete>
  );
};

const StyledAutocomplete = styled.div`
  position: relative;
`;

const PlacesContainer = styled.div`
  position: relative;
  width: 100%;
`;

const PlacesDropDown = styled.div`
  background-color: ${getColor('base000')};
  border: 1px solid;
  border-color: ${getColor('base200')};
  border-radius: ${getRadii('tiny')};
  position: absolute;
  top: ${getSpace('small3')};
  margin-top: -0.25rem;
  width: 100%;
  z-index: ${getZIndex('above')};
  cursor: pointer;
`;

const DropDownItem = styled.div<{ isActive: boolean }>`
  padding: ${getSpace('normal1')};
  user-select: none;

  ${({ isActive }) =>
    isActive &&
    css`
      background-color: ${getColor('evening')};
    `};

  :hover {
    background-color: ${getColor('evening')};
  }
`;

const PlaceText = styled.span<{ shouldHighlight: boolean }>`
  font-weight: ${getFontWeight('regular')};
  ${({ shouldHighlight }) =>
    shouldHighlight &&
    css`
      color: ${getColor('base900')};
      font-weight: ${getFontWeight('semibold')};
    `};
`;

export default AddressAutocomplete;
